import type { MessageParameters, TranslationKey, Translate } from "../i18n/translate";
import { formatDate, formatMonth } from "./format";
import { optionalMarkAttribute } from "./formErrors";

/** A form control that takes part in the constraint validation of its form. */
export type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** A control that does not meet its constraints, with the message to show (F-60, UX-008). */
export interface FormProblem {
  /** The control. */
  readonly control: FormControl;
  /** Identifier of the control; empty for a control without one. */
  readonly controlId: string;
  /**
   * Its visible label without the „(optional)“ mark, preceded by the legend of its group when other
   * controls of the form share the label; empty for a control without a label.
   */
  readonly label: string;
  /** What to correct, in the language of the interface. */
  readonly message: string;
}

/** What kind of value a control takes; it chooses the wording of a message. */
type ValueKind = "text" | "number" | "date" | "month" | "time" | "choice" | "checkbox" | "file";

/** Kind of value by input type; every other type takes text. */
const kindByInputType: Readonly<Record<string, ValueKind>> = {
  number: "number",
  range: "number",
  date: "date",
  month: "month",
  week: "time",
  time: "time",
  "datetime-local": "time",
  radio: "choice",
  checkbox: "checkbox",
  file: "file",
};

/** Message of a missing value by kind. */
const missingKey: Readonly<Record<ValueKind, TranslationKey>> = {
  text: "form.error.missing",
  number: "form.error.missingNumber",
  date: "form.error.missingDate",
  month: "form.error.missingMonth",
  time: "form.error.missingTime",
  choice: "form.error.missingChoice",
  checkbox: "form.error.missingCheckbox",
  file: "form.error.missingFile",
};

/** Message of an input the browser cannot read as a value of its kind. */
const badInputKey: Readonly<Record<ValueKind, TranslationKey>> = {
  text: "form.error.invalid",
  number: "form.error.number",
  date: "form.error.incompleteDate",
  month: "form.error.incompleteDate",
  time: "form.error.incompleteTime",
  choice: "form.error.invalid",
  checkbox: "form.error.invalid",
  file: "form.error.invalid",
};

/** A pattern of digits with a length, as the DATEV fields use it (`[0-9]{4,7}`). */
const digitsPattern = /^\[0-9\]\{(\d+)(?:,(\d+))?\}$/u;

/** A pattern of letters and digits with a length (`[A-Za-z0-9]{1,8}`). */
const lettersAndDigitsPattern = /^\[A-Za-z0-9\]\{(\d+)(?:,(\d+))?\}$/u;

/** Messages of a pattern with a length: an exact length or a range of lengths. */
interface LengthPatternKeys {
  readonly exactly: TranslationKey;
  readonly between: TranslationKey;
}

/** Messages of a pattern of digits. */
const digitsKeys: LengthPatternKeys = {
  exactly: "form.error.digitsExactly",
  between: "form.error.digitsBetween",
};

/** Messages of a pattern of letters and digits. */
const lettersAndDigitsKeys: LengthPatternKeys = {
  exactly: "form.error.lettersAndDigitsExactly",
  between: "form.error.lettersAndDigitsBetween",
};

/** A message key with its parameters. */
interface Message {
  readonly key: TranslationKey;
  readonly parameters?: MessageParameters;
}

/**
 * Checks whether a form element takes part in the constraint validation as a control with a
 * value (buttons, fieldsets and outputs do not).
 *
 * @param element - An element of the form.
 * @returns `true` for an input, a select or a text area.
 */
function isFormControl(element: Element): element is FormControl {
  const control =
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement;

  return control;
}

/**
 * Determines the kind of value of a control.
 *
 * @param control - The control.
 * @returns The kind.
 */
function valueKind(control: FormControl): ValueKind {
  let kind: ValueKind = "text";

  if (control instanceof HTMLSelectElement) {
    kind = "choice";
  } else if (control instanceof HTMLInputElement) {
    kind = kindByInputType[control.type] ?? "text";
  }

  return kind;
}

/**
 * Formats a bound of a range (`min`, `max`) for a message: numbers in the format of the language
 * (the translation formats them), dates as `01.09.2026`, months as `September 2026`.
 *
 * @param bound - The attribute value.
 * @param kind - Kind of value of the control.
 * @param languageTag - BCP 47 tag.
 * @returns The value to insert.
 */
function formatBound(bound: string, kind: ValueKind, languageTag: string): string | number {
  let formatted: string | number = bound;

  if (kind === "number") {
    formatted = Number(bound);
  } else if (kind === "date") {
    formatted = formatDate(bound, languageTag);
  } else if (kind === "month") {
    formatted = formatMonth(bound, languageTag);
  }

  return formatted;
}

/**
 * Message of a range violation.
 *
 * @param control - The control.
 * @param kind - Its kind of value.
 * @param below - `true` below the minimum, `false` above the maximum.
 * @param languageTag - BCP 47 tag.
 * @returns The message.
 */
function rangeMessage(
  control: FormControl,
  kind: ValueKind,
  below: boolean,
  languageTag: string,
): Message {
  const bound = control instanceof HTMLInputElement ? (below ? control.min : control.max) : "";
  const value = formatBound(bound, kind, languageTag);
  const numeric = kind === "number";
  let message: Message;

  if (below) {
    message = {
      key: numeric ? "form.error.min" : "form.error.earliest",
      parameters: { min: value },
    };
  } else {
    message = { key: numeric ? "form.error.max" : "form.error.latest", parameters: { max: value } };
  }

  return message;
}

/**
 * Number of decimal places a step allows, or `null` if the step is no power of ten below one
 * (`0.01` allows two, `0.5` is no such step).
 *
 * @param step - The step.
 * @returns The decimal places or `null`.
 */
function decimalPlaces(step: number): number | null {
  const places = Math.round(-Math.log10(step));
  const allowed = places > 0 && Math.abs(10 ** -places - step) < 1e-12 ? places : null;

  return allowed;
}

/**
 * Message of a value between the allowed steps: whole numbers, decimal places or steps of a size.
 *
 * @param control - The control.
 * @param kind - Its kind of value.
 * @returns The message.
 */
function stepMessage(control: FormControl, kind: ValueKind): Message {
  const input = control instanceof HTMLInputElement ? control : null;
  const step = input === null || input.step === "" ? 1 : Number(input.step);
  const base = input === null || input.min === "" ? 0 : Number(input.min);
  const ratio = base / step;
  const fromZero = Number.isFinite(ratio) && Math.abs(ratio - Math.round(ratio)) < 1e-9;
  const places = decimalPlaces(step);
  const numeric = kind === "number" && Number.isFinite(step) && step > 0;
  let message: Message = { key: "form.error.invalid" };

  if (numeric && step === 1 && fromZero) {
    message = { key: "form.error.wholeNumber" };
  } else if (numeric && places !== null && fromZero) {
    message = { key: "form.error.decimals", parameters: { count: places } };
  } else if (numeric) {
    message = { key: "form.error.step", parameters: { step } };
  }

  return message;
}

/**
 * Message of a value that does not match the pattern of its control. Patterns of digits or of
 * letters and digits with a length are described; any other pattern refers to the hint of the
 * field.
 *
 * @param control - The control.
 * @returns The message.
 */
function patternMessage(control: FormControl): Message {
  const pattern = control instanceof HTMLInputElement ? control.pattern : "";
  const digits = digitsPattern.exec(pattern);
  const lettersAndDigits = lettersAndDigitsPattern.exec(pattern);
  const match = digits ?? lettersAndDigits;
  const keys = digits === null ? lettersAndDigitsKeys : digitsKeys;
  let message: Message = { key: "form.error.pattern" };

  if (pattern === "[0-9]*") {
    message = { key: "form.error.digitsOnly" };
  } else if (match !== null) {
    const min = Number(match[1]);
    const max = match[2] === undefined ? min : Number(match[2]);

    message =
      min === max
        ? { key: keys.exactly, parameters: { count: min } }
        : { key: keys.between, parameters: { min, max } };
  }

  return message;
}

/**
 * Chooses the message of the first constraint a control does not meet. A message the control set
 * itself (`setCustomValidity`) is shown as it is.
 *
 * @param control - An invalid control.
 * @param t - Translation function.
 * @param languageTag - BCP 47 tag for numbers and dates.
 * @returns The message in the language of the interface.
 */
function constraintMessage(control: FormControl, t: Translate, languageTag: string): string {
  const validity = control.validity;
  const kind = valueKind(control);
  let message: Message = { key: "form.error.invalid" };

  if (validity.valueMissing) {
    message = { key: missingKey[kind] };
  } else if (validity.badInput) {
    message = { key: badInputKey[kind] };
  } else if (validity.typeMismatch) {
    message = { key: control.type === "email" ? "form.error.email" : "form.error.url" };
  } else if (validity.patternMismatch) {
    message = patternMessage(control);
  } else if (validity.tooShort && !(control instanceof HTMLSelectElement)) {
    message = { key: "form.error.tooShort", parameters: { count: control.minLength } };
  } else if (validity.tooLong && !(control instanceof HTMLSelectElement)) {
    message = { key: "form.error.tooLong", parameters: { count: control.maxLength } };
  } else if (validity.rangeUnderflow) {
    message = rangeMessage(control, kind, true, languageTag);
  } else if (validity.rangeOverflow) {
    message = rangeMessage(control, kind, false, languageTag);
  } else if (validity.stepMismatch) {
    message = stepMessage(control, kind);
  }

  const text =
    validity.customError && control.validationMessage !== ""
      ? control.validationMessage
      : t(message.key, message.parameters);

  return text;
}

/**
 * Returns the visible label of a control for the error summary: the elements named by
 * `aria-labelledby`, else `aria-label`, else the text of its first label without the control
 * itself (a label may wrap it) and without the „(optional)“ mark; a trailing colon or full stop is
 * dropped, since the summary adds a colon.
 *
 * @param control - The control.
 * @returns The label, or an empty text.
 */
function controlLabel(control: FormControl): string {
  const labelledBy = (control.getAttribute("aria-labelledby") ?? "")
    .split(" ")
    .filter((id) => id !== "")
    .map((id) => control.ownerDocument.getElementById(id)?.textContent ?? "")
    .join(" ");
  const label = control.labels?.[0];
  let text = labelledBy;

  if (text.trim() === "") {
    text = control.getAttribute("aria-label") ?? "";
  }

  if (text.trim() === "" && label !== undefined) {
    const copy = label.cloneNode(true) as HTMLElement;

    copy
      .querySelectorAll(`input, select, textarea, [${optionalMarkAttribute}]`)
      .forEach((element) => {
        element.remove();
      });
    text = copy.textContent;
  }

  const normalized = text.replace(/\s+/gu, " ").trim().replace(/[.:]$/u, "");

  return normalized;
}

/**
 * Returns the legend of the innermost group (`fieldset`) of a control inside its form, for example
 * „Vertrag“ for the name of the contract contact.
 *
 * @param control - The control.
 * @param form - Its form.
 * @returns The legend, or an empty text outside a group.
 */
function groupLegend(control: FormControl, form: HTMLFormElement): string {
  const group = control.closest("fieldset");
  const legend =
    group === null || !form.contains(group) ? null : group.querySelector(":scope > legend");
  const text = (legend?.textContent ?? "").replace(/\s+/gu, " ").trim();

  return text;
}

/**
 * Finds every control of a form that does not meet its constraints (`required`, `min`, `max`,
 * `step`, `pattern`, `minlength`, the type of an e-mail, date or number field), in document order,
 * with a message in the language of the interface. Disabled and hidden controls are skipped, as
 * the browser does; a group of radio buttons is reported once. A label that several controls of
 * the form share („Name“ of each contact) is preceded by the legend of the control's group
 * („Vertrag – Name“), so every entry of the summary names one field (UX-008, WCAG 3.3.1).
 *
 * @param form - The form.
 * @param t - Translation function.
 * @param languageTag - BCP 47 tag for numbers and dates.
 * @returns The problems; empty if the form can be sent.
 */
export function formProblems(
  form: HTMLFormElement,
  t: Translate,
  languageTag: string,
): readonly FormProblem[] {
  const controls = Array.from(form.elements).filter(isFormControl);
  const invalid = controls.filter((control) => control.willValidate && !control.validity.valid);
  const labels = new Map(controls.map((control) => [control, controlLabel(control)] as const));
  const allLabels = [...labels.values()];
  const shared = (label: string): boolean =>
    allLabels.filter((other) => other === label).length > 1;
  const problems = invalid
    .filter((control, index) => {
      const radio = control instanceof HTMLInputElement && control.type === "radio";
      const firstOfGroup =
        !radio ||
        invalid.findIndex(
          (other) =>
            other instanceof HTMLInputElement &&
            other.type === "radio" &&
            other.name === control.name,
        ) === index;

      return firstOfGroup;
    })
    .map((control) => {
      const label = labels.get(control) ?? "";
      const legend = groupLegend(control, form);
      const problem: FormProblem = {
        control,
        controlId: control.id,
        label: label !== "" && legend !== "" && shared(label) ? `${legend} – ${label}` : label,
        message: constraintMessage(control, t, languageTag),
      };

      return problem;
    });

  return problems;
}
